import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { QRCodeCanvas } from "qrcode.react";
import { Card, CardBody, Col, Container, Row } from "reactstrap";
import PostApiCall from "../../Api";

export default function QRPreview() {
  const [jobId, setJobId] = useState(null);
  const [jobType, setJobType] = useState('QR Job');
  const [jobName, setJobName] = useState("");
  const [codes, setCodes] = useState([]);
  const [baseUrl, setBaseUrl] = useState("");
  const [sheetWidth, setSheetWidth] = useState(350); // mm
  const [sheetHeight, setSheetHeight] = useState(200); // mm
  const [qrSize, setQrSize] = useState(15); // mm
  const [verticalSpacing, setVerticalSpacing] = useState(60); // mm (top of text -> next text top)
  const [horizontalSpacing, setHorizontalSpacing] = useState(35); // mm (left of QR -> next left)
  const [topSpacing, setTopSpacing] = useState(50); // mm (page top -> top of first text)
  const [leftSpacing, setLeftSpacing] = useState(10); // mm (page left -> left of first QR)
  const [fromPage, setFromPage] = useState(1);
  const [toPage, setToPage] = useState(1);
  const [textHeight, setTextHeight] = useState(6); // mm (not required for layout if verticalSpacing already includes)
  const [fontSize, setFontSize] = useState(4); // mm
  const [gap,seGap] = useState(2);
  const [textRotation,seTextRotation] = useState(0);
  // const gapBetweenQrAndTextMm = 3; // mm gap between QR bottom and text top

  // 
  useEffect(() => {
    const localData = localStorage.getItem('QrDeimensions') ? JSON.parse(localStorage.getItem('QrDeimensions')) : null
    if (localData) {
      setJobId(localData?.fld_id);
      setJobName(localData?.fld_job_name);
      setBaseUrl(localData?.fld_base_url);
      setSheetHeight(localData?.fld_sheet_height);
      setSheetWidth(localData?.fld_sheet_width);
      setQrSize(localData?.fld_qr_size);
      setFontSize(localData?.fld_font_size);
      setTextHeight(localData?.fld_text_height);
      setTopSpacing(localData?.fld_top_spacing);
      setLeftSpacing(localData?.fld_left_spacing);
      setVerticalSpacing(localData?.fld_v_spacing);
      setHorizontalSpacing(localData?.fld_h_spacing);
      setFromPage(localData?.fld_from_page);
      setToPage(localData?.fld_to_page);
      setJobType(localData?.fld_job_type);
    }
  }, [])

  // Use 72 dpi conversion (points per inch) to match PDFKit points exactly:
  const mmToPx = (mm) => (mm * 72) / 25.4; // <-- important: 72 not 96

  // pixel equivalents (same units used by backend)
  const qrSizePx = mmToPx(qrSize);
  const leftSpacingPx = mmToPx(leftSpacing);
  const topSpacingPx = mmToPx(topSpacing);
  const horizontalSpacingPx = mmToPx(horizontalSpacing);
  const verticalSpacingPx = mmToPx(verticalSpacing);
  const sheetWidthPx = mmToPx(sheetWidth);
  const sheetHeightPx = mmToPx(sheetHeight);
  const textHeightPx = mmToPx(textHeight);
  const fontSizePx = mmToPx(fontSize);
  const gapPx = mmToPx(gap);

  // compute cols/rows by iterative fit (same method used on backend)
  let cols = 0;
  {
    let curX = leftSpacingPx;
    while (curX + qrSizePx <= sheetWidthPx) {
      cols++;
      curX += horizontalSpacingPx;
      // safety break
      if (cols > 1000) break;
    }
  }

  let rows = 0;
  {
    let curY = topSpacingPx;
    while (curY + qrSizePx <= sheetHeightPx) {
      rows++;
      curY += verticalSpacingPx;
      if (rows > 1000) break;
    }
  }

  // guard: if no cols/rows fit, show at least 1
  if (cols < 1) cols = 1;
  if (rows < 1) rows = 1;

  const perPage = cols * rows;
  const previewCodes = codes.slice(0, perPage);

  // upload excel
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onload = (evt) => {
      const data = new Uint8Array(evt.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { header: 1 });
      const codesList = json.filter((row) => row[0]).map((row) => baseUrl + row[0]);
      setCodes(codesList);
    };
    reader.readAsArrayBuffer(file);
  };

  // generate PDF: send mm values (same names backend expects)
  const generatePDF = async () => {
    var login = localStorage.getItem("LoginDetail");
    var details = JSON.parse(login);
    try {
      const response = await PostApiCall.postRequest({
        jobId: jobId,
        baseUrl: baseUrl,
        jobType: jobType,
        jobName,
        codes,
        qrSize,
        sheetWidth,
        sheetHeight,
        topSpacing,
        leftSpacing,
        rightSpacing: horizontalSpacing,
        bottomSpacing: verticalSpacing,
        fromPage,
        toPage,
        textHeight,
        fontSize,
        updatedBy: details[0].fld_userid,
        createdBy: details[0].fld_userid,
        gap,
        textRotation
      }, "GenerateQrPdf");

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${jobName || "QRCodes"}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error generating PDF:", err);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col xl="12">
            <Card>
              <div className="Bechofy-bg-soft-pink d-flex align-items-center" style={{ height: 60 }}>
                <h4 className="text-white ml-5 pb-0 mb-0">QR Code Generator</h4>
              </div>
              <CardBody className="pt-0">
                {/* --- Inputs (kept same) --- */}
                <Row>
                  <Col lg={12} className="mt-4">
                    <Row>
                      <Col lg={6} className="mb-2">
                        <label className="my-1">Job Name:</label>
                        <input className="form-control" value={jobName} onChange={(e) => setJobName(e.target.value)} />
                      </Col>

                      <Col lg={6} className="mb-2">
                        <label className="my-1">Base URL:</label>
                        <input className="form-control" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} />
                      </Col>

                      <Col lg={12} className="mb-2">
                        <label className="my-1">Upload Excel:</label>
                        <input className="form-control" type="file" style={{ display: "block" }} accept=".xlsx, .xls" onChange={handleFileUpload} />
                      </Col>

                      {/* sizes and spacing inputs */}
                      <Col lg={3} className="mb-2">
                        <label className="my-1">Sheet Width (mm):</label>
                        <input className="form-control" type="number" value={sheetWidth} onChange={(e) => setSheetWidth(+e.target.value)} />
                      </Col>
                      <Col lg={3} className="mb-2">
                        <label className="my-1">Sheet Height (mm):</label>
                        <input className="form-control" type="number" value={sheetHeight} onChange={(e) => setSheetHeight(+e.target.value)} />
                      </Col>
                      <Col lg={3} className="mb-2">
                        <label className="my-1">QR Size (mm):</label>
                        <input className="form-control" type="number" value={qrSize} onChange={(e) => setQrSize(+e.target.value)} />
                      </Col>
                      <Col lg={3} className="mb-2">
                        <label className="my-1">Font Size (mm):</label>
                        <input className="form-control" type="number" value={fontSize} onChange={(e) => setFontSize(+e.target.value)} />
                      </Col>

                      <Col lg={3} className="mb-2">
                        <label className="my-1">Text Height (mm):</label>
                        <input className="form-control" type="number" value={textHeight} onChange={(e) => setTextHeight(+e.target.value)} />
                      </Col>
                      <Col lg={3} className="mb-2">
                        <label className="my-1">Top Spacing (mm):</label>
                        <input className="form-control" type="number" value={topSpacing} onChange={(e) => setTopSpacing(+e.target.value)} />
                      </Col>
                      <Col lg={3} className="mb-2">
                        <label className="my-1">Left Spacing (mm):</label>
                        <input className="form-control" type="number" value={leftSpacing} onChange={(e) => setLeftSpacing(+e.target.value)} />
                      </Col>

                      <Col lg={3} className="mb-2">
                        <label className="my-1">V Spacing (mm):</label>
                        <input className="form-control" type="number" value={verticalSpacing} onChange={(e) => setVerticalSpacing(+e.target.value)} />
                      </Col>
                      <Col lg={3} className="mb-2">
                        <label className="my-1">H Spacing (mm):</label>
                        <input className="form-control" type="number" value={horizontalSpacing} onChange={(e) => setHorizontalSpacing(+e.target.value)} />
                      </Col>
                      <Col lg={3} className="mb-2">
                        <label className="my-1">Gap Between QR and Text (mm):</label>
                        <input className="form-control" type="number" value={gap} onChange={(e) => seGap(+e.target.value)} />
                      </Col>
                      <Col lg={3} className="mb-2">
                        <label className="my-1">Rotation of Text (deg):</label>
                        <input className="form-control" type="number" value={textRotation} onChange={(e) => seTextRotation(+e.target.value)} />
                      </Col>

                      <Col lg={3} className="mb-2">
                        <label className="my-1">From Page:</label>
                        <input className="form-control" type="number" value={fromPage} onChange={(e) => setFromPage(+e.target.value)} />
                      </Col>
                      <Col lg={3} className="mb-2">
                        <label className="my-1">To Page:</label>
                        <input className="form-control" type="number" value={toPage} onChange={(e) => setToPage(+e.target.value)} />
                      </Col>
                    </Row>
                  </Col>

                  <Col lg={12}>
                    <h2 className="text-center">Preview</h2>

                    <div style={{ overflowX: "auto" }}>
                      <div
                        style={{
                          position: "relative",
                          width: `${sheetWidthPx}px`,
                          height: `${sheetHeightPx}px`,
                          border: "1px solid #000",
                          margin: "20px 0",
                          background: "white",
                        }}
                      >
                        {/* Draw grid (optional visual aid) */}
                        {/* {Array.from({length: cols}).map((_,c)=> <div key={'v'+c} style={{position:'absolute', left:`${leftSpacingPx + c*horizontalSpacingPx}px`, top:0, bottom:0, width:1, background:'#eee'}} />)} */}

                        {previewCodes.map((url, i) => {
                          const col = i % cols;
                          const row = Math.floor(i / cols);
                          const x = leftSpacingPx + col * horizontalSpacingPx;
                          const textTop = topSpacingPx + row * verticalSpacingPx;
                          const qrY = textTop; // QR top = textTop (backend uses this)
                          const textY = qrY + qrSizePx + gapPx; // position (visual) of text inside the container

                          return (
                            <div
                              key={i}
                              style={{
                                position: "absolute",
                                left: `${x}px`,
                                top: `${qrY}px`,
                                width: `${qrSizePx}px`,
                                // textAlign: "center",
                              }}
                            >
                              <QRCodeCanvas value={url} size={qrSizePx} />
                              <div
                                style={{
                                  position: "absolute",
                                  top: `${(qrSizePx + gapPx)}px`,
                                  left: 0,
                                  width: `${(qrSizePx + 40)}px`,
                                  fontSize: `${fontSizePx}px`,
                                  wordBreak: "break-all",
                                  fontFamily: "Arial, sans-serif",
                                  fontWeight: "bold",
                                  transform: `rotate(${textRotation}deg)`,
                                  transformOrigin: "left center"
                                }}
                              >
                                {url?.replace(baseUrl, "")}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </Col>
                </Row>

                <div className="sticky-bottom p-2 bg-white">
                  <button onClick={generatePDF} className="btn Bechofy-btn btn-sm float-right">
                    Generate PDF
                  </button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
